import React from 'react';

interface TopEvent {
  title: string;
  groupName: string;
  startDate: string;
  rsvpCount: number;
  averageScore: number;
}

interface GreatContentHighlight {
  userInitials: string;
  userName: string;
  groupName?: string;
  contents: string;
  replyCount?: number;
}

interface Retention {
  cobuBarWidth: number;
  cobuRetentionPct: string;
  nonCobuBarWidth: number;
  nonCobuRetentionPct: string;
  cobuExpiries: number;
  cobuRetained: number;
  nonCobuExpiries: number;
  nonCobuRetained: number;
  totalExpiries: number;
  totalRetained: number;
  totalRetentionPct: string;
  analysisPeriodLabel: string;
  month: string;
}

interface SingleRating {
  value: number;
  label: string;
}

interface BelowGoalMetric {
  summary: string;
  nextStep: string;
}

interface Review {
  reviewerName: string;
  reviewDate: string;
  reviewRating: number;
  reviewText: string;
  reviewLink: string;
  starsHtml?: string;
}

interface ReferralLead {
  leadName: string;
  referredBy: string;
  leadDate: string;
}

interface BestPractice {
  isPass: boolean;
  heading: string;
  goal: string;
  content: string;
  showFAQ?: boolean;
}

interface Props {
  buildingName: string;
  startDate: string;
  endDate: string;
  liveDate: string;
  activeRate: number;
  heroCobuRating: string;
  heroNonCobuRating: string;
  heroCobuReviewPct: number;
  hasRatingComparison?: boolean;
  singleRating?: SingleRating;
  reportMonth: string;
  totalUsers: number;
  adoptionRate: number;
  totalActiveUsers: number;
  portfolioAvgTotalUsers: number;
  portfolioAvgAdoptionRate: number;
  portfolioAvgTotalActiveUsers: number;
  portfolioAvgActiveRate: number;
  adoptionGoalClass?: 'above' | 'below';
  mauCountGoalClass?: 'above' | 'below';
  mauRateGoalClass?: 'above' | 'below';
  showEngageNudge?: boolean;
  belowGoalMetrics?: BelowGoalMetric[];
  eventTotal: number;
  rsvpTotal: number;
  topEvents?: TopEvent[];
  showRsvpNudge?: boolean;
  rsvpAverage?: number;
  rsvpGoal?: number;
  greatContentHighlight?: GreatContentHighlight;
  retentionActive?: boolean;
  retention?: Retention;
  reviews?: Review[];
  cumulativeCobuReviewPct: number | string;
  cumulativeCobuAvgRating: string;
  cumulativeNonCobuAvgRating: string;
  cobuReviewsLaunchDate: string;
  referralsActive?: boolean;
  referralsThisMonth?: ReferralLead[];
  referrals12MonthTotal: number;
  referralsThisMonthCount: number;
  leadsUrl?: string;
  websiteConversionActive?: boolean;
  communityPageUrl?: string;
  bestPractices?: BestPractice[];
  dashboardUrl?: string;
  showAiVisibilityCta?: boolean;
}

const goalColor = (cls?: 'above' | 'below') =>
  cls === 'above' ? '#28a745' : cls === 'below' ? '#dc3545' : '#a0aec0';

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <span style={{ color: '#d4a017' }}>{'★'.repeat(Math.round(rating))}</span>
);

const SectionHeader: React.FC<{ badge: string; title: string }> = ({
  badge,
  title
}) => (
  <div
    style={{
      padding: '14px 28px',
      background: '#f7fafc',
      borderBottom: '2px solid #191D63',
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '1.2px',
        padding: '4px 10px',
        borderRadius: 5,
        background: '#191D63',
        color: '#fff'
      }}
    >
      {badge}
    </span>
    <span style={{ fontSize: 19, fontWeight: 800, color: '#191D63' }}>
      {title}
    </span>
  </div>
);

const MetricCard: React.FC<{
  label: string;
  value: string;
  goalText?: string;
  goalClass?: 'above' | 'below';
  benchmarkText?: string;
}> = ({ label, value, goalText, goalClass, benchmarkText }) => (
  <div
    style={{
      background: '#f7fafc',
      borderRadius: 6,
      padding: '10px 14px',
      border: '1px solid #e2e8f0',
      flex: 1
    }}
  >
    <div style={{ fontSize: 10, color: '#718096', marginBottom: 2 }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 800,
        color: '#191D63',
        lineHeight: 1.2
      }}
    >
      {value}
    </div>
    {goalText && (
      <div
        style={{
          fontSize: 10,
          color: goalColor(goalClass),
          marginTop: 2,
          lineHeight: 1.3
        }}
      >
        {goalText}
      </div>
    )}
    {benchmarkText && (
      <div style={{ fontSize: 10, color: '#a0aec0', marginTop: 2 }}>
        {benchmarkText}
      </div>
    )}
  </div>
);

const NudgeBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      background: '#fff5f5',
      borderLeft: '3px solid #dc3545',
      borderRadius: '0 6px 6px 0',
      padding: '10px 14px',
      fontSize: 13,
      color: '#9b2c2c',
      marginBottom: 12
    }}
  >
    {children}
  </div>
);

const SubDivider = () => (
  <hr
    style={{ border: 'none', borderTop: '1px solid #e8ecf1', margin: '16px 0' }}
  />
);

const IncludedBadge = () => (
  <span
    style={{
      display: 'inline-block',
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      padding: '2px 6px',
      borderRadius: 3,
      background: '#edf2f7',
      color: '#718096',
      marginLeft: 8,
      verticalAlign: 'middle'
    }}
  >
    Included
  </span>
);

const ActivateCard: React.FC<{
  icon: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
}> = ({ icon, title, description, ctaLabel, ctaHref }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      background: '#f7fafc',
      borderRadius: 8,
      padding: '14px 16px',
      border: '1px solid #e2e8f0',
      marginTop: 8
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        background: '#f0f7ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: '#191D63',
          marginBottom: 2
        }}
      >
        {title}
        <IncludedBadge />
      </div>
      <div style={{ fontSize: 12, color: '#718096', lineHeight: 1.4 }}>
        {description}
      </div>
    </div>
    <a
      href={ctaHref}
      style={{
        color: '#00B5D1',
        fontSize: 12,
        fontWeight: 700,
        textDecoration: 'none',
        flexShrink: 0,
        whiteSpace: 'nowrap'
      }}
    >
      {ctaLabel} →
    </a>
  </div>
);

const MonthlyReport: React.FC<Props> = (props) => {
  const {
    buildingName,
    startDate,
    endDate,
    liveDate,
    activeRate,
    heroCobuRating,
    heroNonCobuRating,
    heroCobuReviewPct,
    hasRatingComparison,
    singleRating,
    reportMonth,
    totalUsers,
    adoptionRate,
    totalActiveUsers,
    portfolioAvgTotalUsers,
    portfolioAvgAdoptionRate,
    portfolioAvgTotalActiveUsers,
    portfolioAvgActiveRate,
    adoptionGoalClass,
    mauCountGoalClass,
    mauRateGoalClass,
    showEngageNudge,
    belowGoalMetrics,
    eventTotal,
    rsvpTotal,
    topEvents,
    showRsvpNudge,
    rsvpAverage,
    rsvpGoal,
    greatContentHighlight,
    retentionActive,
    retention,
    reviews,
    cumulativeCobuReviewPct,
    cumulativeCobuAvgRating,
    cumulativeNonCobuAvgRating,
    cobuReviewsLaunchDate,
    referralsActive,
    referralsThisMonth,
    referrals12MonthTotal,
    referralsThisMonthCount,
    leadsUrl,
    websiteConversionActive,
    communityPageUrl,
    bestPractices,
    dashboardUrl,
    showAiVisibilityCta
  } = props;

  const tableHeaderStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '7px 10px',
    background: '#f7fafc',
    color: '#718096',
    fontWeight: 600,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #e2e8f0'
  };

  const tableCellStyle: React.CSSProperties = {
    padding: '9px 10px',
    borderBottom: '1px solid #f0f2f5',
    color: '#2d3748',
    fontSize: 12
  };

  return (
    <div
      style={{
        fontFamily:
          "'Work Sans', Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        background: '#ffffff',
        borderRadius: 12,
        overflow: 'hidden',
        color: '#2d3748',
        lineHeight: 1.6
      }}
    >
      <style>{`
        .mobile-stack { display: none; }
        .stack-card {
          background: #f7fafc; border-radius: 8px; padding: 10px 12px;
          border: 1px solid #e2e8f0; margin-bottom: 8px;
        }
        .stack-row { padding: 3px 0; font-size: 12px; }
        .stack-label {
          color: #718096; font-weight: 700; text-transform: uppercase;
          font-size: 9px; letter-spacing: 0.4px; display: block; margin-bottom: 1px;
        }
        @media screen and (max-width: 600px) {
          .desktop-table { display: none !important; }
          .mobile-stack { display: block !important; }
        }
      `}</style>
      {/* HERO */}
      <div
        style={{
          background: 'linear-gradient(135deg, #191D63 0%, #252a6e 100%)',
          color: '#fff',
          padding: '28px 28px 22px'
        }}
      >
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: '-0.5px',
            marginBottom: 2
          }}
        >
          Cobu
        </div>
        <div
          style={{
            fontSize: 11,
            opacity: 0.7,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            fontWeight: 500,
            marginBottom: 18
          }}
        >
          Resident-Powered Occupancy Engine
        </div>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 3 }}>
          Monthly Report
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 3 }}>
          {buildingName}
        </div>
        <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 20 }}>
          {startDate} – {endDate} · Live since {liveDate}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 14,
            flexWrap: 'wrap'
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 200,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 8,
              padding: '12px 16px',
              border: '1px solid rgba(255,255,255,0.18)'
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                opacity: 0.85,
                marginBottom: 6
              }}
            >
              Monthly Active User Rate (MAU)
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#7BE0F0'
              }}
            >
              {activeRate}%
            </div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
              Goal (min): 60%+
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 200,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 8,
              padding: '12px 16px',
              border: '1px solid rgba(255,255,255,0.18)'
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                opacity: 0.85,
                marginBottom: 6
              }}
            >
              Google Reviews Impact
            </div>
            {hasRatingComparison ? (
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: '#7BE0F0'
                }}
              >
                {heroCobuRating} vs {heroNonCobuRating} stars
              </div>
            ) : singleRating ? (
              <>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    color: '#7BE0F0'
                  }}
                >
                  {singleRating.value} stars ({singleRating.label})
                </div>
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                  Comparison not available yet
                </div>
              </>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
                Review rating not available yet
              </div>
            )}
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
              {heroCobuReviewPct}% of reviews are from Cobu
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          Here's what Cobu delivered for {buildingName} this month.
        </div>
      </div>

      {/* AI VISIBILITY CTA (CobuAI-branded handoff, rest of report stays Cobu-branded) */}
      {showAiVisibilityCta && (
        <div style={{ background: '#0d1017', padding: '16px 28px' }}>
          <img
            src='https://framerusercontent.com/images/dINqF5M5ZEBh7HKVhvihYYIqPo.png'
            alt='CobuAI'
            width={36}
            height={36}
            style={{ borderRadius: 8, marginBottom: 10, display: 'block' }}
          />
          <div style={{ fontSize: 13, color: '#f6f1e7', lineHeight: 1.5 }}>
            1 in 2 renters now use AI to find their apartment (J Turner, August
            2026). CobuAI turns your community's everyday activity on Cobu into
            content that helps your community show up when renters search with
            AI.
          </div>
          <div style={{ marginTop: 12 }}>
            <a
              href='https://www.cobuai.com/demo'
              style={{
                display: 'inline-block',
                background: '#e8a93d',
                color: '#0d1017',
                fontSize: 13,
                fontWeight: 700,
                padding: '9px 18px',
                borderRadius: 20,
                textDecoration: 'none',
                marginRight: 16
              }}
            >
              See how CobuAI works →
            </a>
            <a
              href='mailto:customersuccess@livecobu.com'
              style={{
                color: '#57b8cf',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none'
              }}
            >
              Email us
            </a>
          </div>
        </div>
      )}

      {/* ENGAGE */}
      <div style={{ borderBottom: '1px solid #e8ecf1' }}>
        <SectionHeader badge='ENGAGE' title='Your Community Foundation' />
        <div style={{ padding: '16px 28px 18px' }}>
          <div
            style={{
              fontSize: 13,
              color: '#718096',
              marginBottom: 12,
              lineHeight: 1.5
            }}
          >
            Engagement drives everything – retention, reviews, referrals, and
            website content.
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 10,
              flexWrap: 'wrap'
            }}
          >
            <MetricCard
              label='Total Cobu Users'
              value={String(totalUsers)}
              benchmarkText={`Cobu Portfolio Avg: ${portfolioAvgTotalUsers}`}
            />
            <MetricCard
              label='Adoption Rate'
              value={`${adoptionRate}%`}
              goalText='Goal (min): 50%+*'
              goalClass={adoptionGoalClass}
              benchmarkText={`Cobu Portfolio Avg: ${portfolioAvgAdoptionRate}%`}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 10,
              flexWrap: 'wrap'
            }}
          >
            <MetricCard
              label='Monthly Active Users (MAUs)'
              value={String(totalActiveUsers)}
              goalText='Goal (min): 75+'
              goalClass={mauCountGoalClass}
              benchmarkText={`Cobu Portfolio Avg: ${portfolioAvgTotalActiveUsers}`}
            />
            <MetricCard
              label='Monthly Active User Rate (MAU)'
              value={`${activeRate}%`}
              goalText='Goal (min): 60%+*'
              goalClass={mauRateGoalClass}
              benchmarkText={`Cobu Portfolio Avg: ${portfolioAvgActiveRate}%`}
            />
          </div>
          <div
            style={{
              fontSize: 10,
              color: '#a0aec0',
              fontStyle: 'italic',
              marginBottom: 10
            }}
          >
            *75+ MAUs is the critical mass needed. 50%+ adoption and 60%+ MAU
            rate typically gets you there.
          </div>

          {showEngageNudge && (
            <NudgeBox>
              <div style={{ fontWeight: 600 }}>
                Below goal for {reportMonth}:
              </div>
              {belowGoalMetrics?.map((metric) => (
                <div key={metric.summary} style={{ marginTop: 4 }}>
                  <strong>{metric.summary}.</strong> Next step:{' '}
                  {metric.nextStep}
                </div>
              ))}
            </NudgeBox>
          )}

          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#191D63',
              marginBottom: 4,
              marginTop: 14
            }}
          >
            Event Summary
          </div>
          <div style={{ fontSize: 13, color: '#4a5568', marginBottom: 10 }}>
            Last month had {eventTotal} {eventTotal === 1 ? 'event' : 'events'}{' '}
            with {rsvpTotal} {rsvpTotal === 1 ? 'RSVP' : 'RSVPs'}
          </div>
          {topEvents && topEvents.length > 0 && (
            <>
              <table
                className='desktop-table'
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  marginBottom: 14
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Top Events</th>
                    <th style={tableHeaderStyle}>Group</th>
                    <th style={tableHeaderStyle}>Date</th>
                    <th style={tableHeaderStyle}>RSVPs</th>
                    <th style={tableHeaderStyle}>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {topEvents.map((event, i) => (
                    <tr key={i}>
                      <td style={tableCellStyle}>{event.title}</td>
                      <td style={tableCellStyle}>{event.groupName}</td>
                      <td style={tableCellStyle}>{event.startDate}</td>
                      <td style={tableCellStyle}>{event.rsvpCount}</td>
                      <td style={tableCellStyle}>{event.averageScore}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className='mobile-stack' style={{ display: 'none' }}>
                {topEvents.map((event, i) => (
                  <div className='stack-card' key={i}>
                    <div className='stack-row'>
                      <span className='stack-label'>Event</span>
                      {event.title}
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Group</span>
                      {event.groupName}
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Date</span>
                      {event.startDate}
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>RSVPs</span>
                      {event.rsvpCount}
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Rating</span>
                      {event.averageScore}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {showRsvpNudge && (
            <NudgeBox>
              RSVP average ({rsvpAverage}) is below the goal of {rsvpGoal} – see{' '}
              <a
                href='#best-practices'
                style={{
                  color: '#9b2c2c',
                  fontWeight: 600,
                  textDecoration: 'underline'
                }}
              >
                Best Practices
              </a>{' '}
              below.
            </NudgeBox>
          )}

          {greatContentHighlight && (
            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#718096',
                  marginBottom: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Recent community engagement examples:
              </div>
              <div
                style={{
                  background: '#f7fafc',
                  borderRadius: 8,
                  padding: '10px 14px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#00B5D1',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0
                    }}
                  >
                    {greatContentHighlight.userInitials}
                  </div>
                  <span
                    style={{ fontWeight: 600, fontSize: 13, color: '#2d3748' }}
                  >
                    {greatContentHighlight.userName}
                  </span>
                  {greatContentHighlight.groupName && (
                    <span
                      style={{ fontSize: 11, color: '#a0aec0', marginLeft: 2 }}
                    >
                      · {greatContentHighlight.groupName}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: '#4a5568',
                    marginTop: 8,
                    lineHeight: 1.45,
                    fontStyle: 'italic'
                  }}
                >
                  "{greatContentHighlight.contents}"
                </div>
                {!!greatContentHighlight.replyCount && (
                  <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 6 }}>
                    {greatContentHighlight.replyCount}{' '}
                    {greatContentHighlight.replyCount === 1
                      ? 'reply'
                      : 'replies'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RETAIN */}
      <div style={{ borderBottom: '1px solid #e8ecf1' }}>
        <SectionHeader badge='RETAIN' title='Keeping Your Residents' />
        <div style={{ padding: '16px 28px 18px' }}>
          {retentionActive && retention ? (
            <>
              <div
                style={{
                  fontSize: 13,
                  color: '#718096',
                  marginBottom: 12,
                  lineHeight: 1.5
                }}
              >
                Lease retention, cumulative through {retention.month} –
                comparing Cobu users to non-users at {buildingName}.
              </div>
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      width: 110,
                      fontSize: 13,
                      color: '#4a5568',
                      flexShrink: 0
                    }}
                  >
                    Cobu Users
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: '#edf2f7',
                      borderRadius: 6,
                      overflow: 'hidden',
                      height: 32
                    }}
                  >
                    <div
                      style={{
                        width: `${retention.cobuBarWidth}%`,
                        height: '100%',
                        background: '#28a745',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 10,
                        fontSize: 14,
                        fontWeight: 800,
                        color: 'white'
                      }}
                    >
                      {retention.cobuRetentionPct}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 110,
                      fontSize: 13,
                      color: '#4a5568',
                      flexShrink: 0
                    }}
                  >
                    Non-Cobu
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: '#edf2f7',
                      borderRadius: 6,
                      overflow: 'hidden',
                      height: 32
                    }}
                  >
                    <div
                      style={{
                        width: `${retention.nonCobuBarWidth}%`,
                        height: '100%',
                        background: '#dc3545',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 10,
                        fontSize: 14,
                        fontWeight: 800,
                        color: 'white'
                      }}
                    >
                      {retention.nonCobuRetentionPct}
                    </div>
                  </div>
                </div>
              </div>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  marginBottom: 8
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Cohort</th>
                    <th style={tableHeaderStyle}>Expiring</th>
                    <th style={tableHeaderStyle}>Retained</th>
                    <th style={tableHeaderStyle}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tableCellStyle}>
                      <strong>Cobu Users</strong>
                    </td>
                    <td style={tableCellStyle}>{retention.cobuExpiries}</td>
                    <td style={tableCellStyle}>{retention.cobuRetained}</td>
                    <td
                      style={{
                        ...tableCellStyle,
                        color: '#28a745',
                        fontWeight: 700
                      }}
                    >
                      {retention.cobuRetentionPct}
                    </td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}>
                      <strong>Non-Cobu</strong>
                    </td>
                    <td style={tableCellStyle}>{retention.nonCobuExpiries}</td>
                    <td style={tableCellStyle}>{retention.nonCobuRetained}</td>
                    <td
                      style={{
                        ...tableCellStyle,
                        color: '#dc3545',
                        fontWeight: 700
                      }}
                    >
                      {retention.nonCobuRetentionPct}
                    </td>
                  </tr>
                  <tr style={{ background: '#f7fafc' }}>
                    <td style={tableCellStyle}>
                      <strong>Total</strong>
                    </td>
                    <td style={tableCellStyle}>
                      <strong>{retention.totalExpiries}</strong>
                    </td>
                    <td style={tableCellStyle}>
                      <strong>{retention.totalRetained}</strong>
                    </td>
                    <td style={tableCellStyle}>
                      <strong>{retention.totalRetentionPct}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 4 }}>
                {retention.analysisPeriodLabel}
              </div>
            </>
          ) : (
            <ActivateCard
              icon='🏠'
              title='Retention Analysis'
              description='Compare renewal rates of Cobu users vs. non-users. Requires PMS integration.'
              ctaLabel='Get started'
              ctaHref='mailto:customersuccess@livecobu.com'
            />
          )}
        </div>
      </div>

      {/* ATTRACT */}
      <div style={{ borderBottom: '1px solid #e8ecf1' }}>
        <SectionHeader badge='ATTRACT' title='Bringing In New Residents' />
        <div style={{ padding: '16px 28px 18px' }}>
          <div
            style={{
              fontSize: 13,
              color: '#718096',
              marginBottom: 14,
              lineHeight: 1.5
            }}
          >
            Three channels bringing in new residents.
          </div>

          {/* Google Reviews */}
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#191D63',
              marginBottom: 8,
              marginTop: 0
            }}
          >
            Google Reviews
          </div>
          {reviews && reviews.length > 0 && (
            <>
              <table
                className='desktop-table'
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  marginBottom: 14
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Reviewer</th>
                    <th style={tableHeaderStyle}>Date</th>
                    <th style={tableHeaderStyle}>Rating</th>
                    <th style={tableHeaderStyle}>Review</th>
                    <th style={tableHeaderStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review, i) => (
                    <tr key={i}>
                      <td style={tableCellStyle}>
                        <strong>{review.reviewerName}</strong>
                      </td>
                      <td style={tableCellStyle}>{review.reviewDate}</td>
                      <td style={tableCellStyle}>
                        <Stars rating={review.reviewRating} />
                      </td>
                      <td
                        style={{
                          ...tableCellStyle,
                          color: '#718096',
                          fontSize: 11
                        }}
                      >
                        {review.reviewText}
                      </td>
                      <td style={tableCellStyle}>
                        <a
                          href={review.reviewLink}
                          style={{
                            color: '#00B5D1',
                            textDecoration: 'none',
                            fontSize: 11,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className='mobile-stack' style={{ display: 'none' }}>
                {reviews.map((review, i) => (
                  <div className='stack-card' key={i}>
                    <div className='stack-row'>
                      <span className='stack-label'>Reviewer</span>
                      <strong>{review.reviewerName}</strong>
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Date</span>
                      {review.reviewDate}
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Rating</span>
                      <Stars rating={review.reviewRating} />
                    </div>
                    <div className='stack-row'>
                      <span className='stack-label'>Review</span>
                      <span style={{ color: '#718096', fontSize: 11 }}>
                        {review.reviewText}
                      </span>
                    </div>
                    <div className='stack-row'>
                      <a
                        href={review.reviewLink}
                        style={{
                          color: '#00B5D1',
                          textDecoration: 'none',
                          fontSize: 11
                        }}
                      >
                        View →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 14,
              paddingTop: 14,
              borderTop: '1px dashed #e2e8f0',
              flexWrap: 'wrap'
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 150,
                textAlign: 'center',
                padding: 10,
                background: '#f0f7ff',
                borderRadius: 8,
                border: '1px solid #d0e3f7'
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 800, color: '#191D63' }}>
                {cumulativeCobuReviewPct}% of Reviews
              </div>
              <div style={{ fontSize: 11, color: '#4a5568', marginTop: 2 }}>
                Generated by Cobu*
              </div>
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 150,
                textAlign: 'center',
                padding: 10,
                background: '#f0f7ff',
                borderRadius: 8,
                border: '1px solid #d0e3f7'
              }}
            >
              {hasRatingComparison ? (
                <>
                  <div
                    style={{ fontSize: 20, fontWeight: 800, color: '#191D63' }}
                  >
                    {cumulativeCobuAvgRating} vs {cumulativeNonCobuAvgRating}{' '}
                    stars
                  </div>
                  <div style={{ fontSize: 11, color: '#4a5568', marginTop: 2 }}>
                    Cobu avg vs. non-Cobu avg*
                  </div>
                </>
              ) : singleRating ? (
                <>
                  <div
                    style={{ fontSize: 20, fontWeight: 800, color: '#191D63' }}
                  >
                    {singleRating.value} stars
                  </div>
                  <div style={{ fontSize: 11, color: '#4a5568', marginTop: 2 }}>
                    {singleRating.label}* · Comparison not available yet
                  </div>
                </>
              ) : (
                <div
                  style={{ fontSize: 15, fontWeight: 800, color: '#191D63' }}
                >
                  Review rating not available yet
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              fontSize: 11,
              color: '#a0aec0',
              marginTop: 8,
              fontStyle: 'italic'
            }}
          >
            *Since Cobu Google reviews went live on {cobuReviewsLaunchDate}
          </div>
          <div
            style={{
              fontSize: 11,
              color: '#a0aec0',
              marginTop: 4,
              fontStyle: 'italic'
            }}
          >
            82% of renters read reviews before choosing; 4+ stars is the
            minimum. — Satisfacts
          </div>

          <SubDivider />

          {/* Referrals */}
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#191D63',
              marginBottom: 4
            }}
          >
            Resident Referrals
          </div>
          {referralsActive ? (
            <>
              {referralsThisMonth && referralsThisMonth.length > 0 && (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    marginTop: 8,
                    marginBottom: 12
                  }}
                >
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>New This Month</th>
                      <th style={tableHeaderStyle}>Referred By</th>
                      <th style={tableHeaderStyle}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralsThisMonth.map((lead, i) => (
                      <tr key={i}>
                        <td style={tableCellStyle}>
                          <strong>{lead.leadName}</strong>
                        </td>
                        <td style={tableCellStyle}>{lead.referredBy}</td>
                        <td style={tableCellStyle}>{lead.leadDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div
                style={{
                  fontSize: 13,
                  color: '#4a5568',
                  padding: '12px 14px',
                  background: '#f7fafc',
                  borderRadius: 6,
                  borderLeft: '3px solid #00B5D1',
                  lineHeight: 1.45
                }}
              >
                <strong>
                  {referrals12MonthTotal}{' '}
                  {referrals12MonthTotal === 1 ? 'referral' : 'referrals'}
                </strong>{' '}
                over the past 12 months ·{' '}
                <strong>{referralsThisMonthCount} new</strong> this month.{' '}
                {leadsUrl && (
                  <a
                    href={leadsUrl}
                    style={{ color: '#00B5D1', fontWeight: 600 }}
                  >
                    View leads →
                  </a>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#a0aec0',
                  marginTop: 6,
                  fontStyle: 'italic'
                }}
              >
                Referral-to-lease conversion averages ~50%.
              </div>
            </>
          ) : (
            <ActivateCard
              icon='🤝'
              title='Referral Tracking'
              description='Track resident-to-resident referrals and measure lead quality.'
              ctaLabel='Activate'
              ctaHref='mailto:customersuccess@livecobu.com'
            />
          )}

          <SubDivider />

          {/* Website Conversion */}
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#191D63',
              marginBottom: 4
            }}
          >
            Website Conversion
          </div>
          {websiteConversionActive ? (
            <>
              <div
                style={{
                  fontSize: 13,
                  color: '#4a5568',
                  padding: '12px 14px',
                  background: '#f7fafc',
                  borderRadius: 6,
                  borderLeft: '3px solid #00B5D1',
                  lineHeight: 1.45,
                  marginTop: 8
                }}
              >
                Your Cobu-powered community page is live.{' '}
                {communityPageUrl && (
                  <a
                    href={communityPageUrl}
                    style={{ color: '#00B5D1', fontWeight: 600 }}
                  >
                    View page →
                  </a>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#a0aec0',
                  marginTop: 6,
                  fontStyle: 'italic'
                }}
              >
                Visitors who view this page convert at 2x the rate.
              </div>
            </>
          ) : (
            <ActivateCard
              icon='🌐'
              title='Community Page'
              description='Add a Cobu-powered community page to your website to convert prospects.'
              ctaLabel='Activate'
              ctaHref='mailto:customersuccess@livecobu.com'
            />
          )}
        </div>
      </div>

      {/* BEST PRACTICES */}
      {bestPractices && bestPractices.length > 0 && (
        <div id='best-practices' style={{ borderBottom: '1px solid #e8ecf1' }}>
          <SectionHeader
            badge='BEST PRACTICES'
            title='How to Drive Even More Value'
          />
          <div style={{ padding: '16px 28px 18px' }}>
            <div
              style={{
                fontSize: 13,
                color: '#718096',
                marginBottom: 12,
                lineHeight: 1.5
              }}
            >
              Where {buildingName} stands on key practices.
            </div>
            {bestPractices.map((practice, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid #f0f2f5'
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: practice.isPass ? '#28a745' : '#dc3545',
                    flexShrink: 0,
                    marginTop: 2
                  }}
                />
                <div style={{ width: 160, flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#191D63'
                    }}
                  >
                    {practice.heading}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: '#718096',
                      marginTop: 2
                    }}
                  >
                    {practice.goal}
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    fontSize: 14,
                    color: '#333',
                    lineHeight: 1.4
                  }}
                >
                  {/* Safe: content is authored via our controlled backend, never user-generated */}
                  <span
                    dangerouslySetInnerHTML={{ __html: practice.content }}
                  />
                  {practice.showFAQ && (
                    <>
                      {' '}
                      <a href='https://knowledge.cobuai.com/knowledge/cobu-event-messaging-faq'>
                        FAQ
                      </a>
                      .
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div
        style={{
          background: '#f7fafc',
          padding: '24px 28px',
          textAlign: 'center'
        }}
      >
        {dashboardUrl && (
          <a
            href={dashboardUrl}
            style={{
              display: 'inline-block',
              background: '#00B5D1',
              color: 'white',
              fontSize: 15,
              fontWeight: 700,
              padding: '12px 36px',
              borderRadius: 30,
              textDecoration: 'none',
              marginBottom: 14
            }}
          >
            View Your Full Dashboard →
          </a>
        )}
        <div style={{ fontSize: 13, color: '#4a5568', marginBottom: 8 }}>
          Questions? Your Cobu team is here to help:{' '}
          <a
            href='mailto:customersuccess@livecobu.com'
            style={{ color: '#00B5D1', textDecoration: 'none' }}
          >
            customersuccess@livecobu.com
          </a>
        </div>
        <div style={{ fontSize: 12, color: '#718096', marginBottom: 18 }}>
          Need event inspiration?{' '}
          <a
            href='https://sites.google.com/livecobu.com/premium/months?authuser=0'
            style={{ color: '#00B5D1', textDecoration: 'none' }}
          >
            Browse event ideas for every month →
          </a>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#191D63' }}>
            Cobu
          </div>
          <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 2 }}>
            Empowering everyone to feel like they belong wherever they live.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReport;
